import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Kanban, DollarSign, ShieldAlert, Calendar, FileCheck, MessageSquareText, HeartHandshake, Trophy, ListTodo, Building, TriangleAlert, Brain, TrendingUp, Activity, Heart, GripVertical } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { aud, fmtDate, BACKEND_URL } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Stat } from "../Common";
import { ACCOUNT_NAV } from "../AppShell";
import { quoteOfTheDay } from "../../data/quotes";
import { KeyDatesWidget } from "./KeyDates";
import { LibraryWidget } from "./LibraryWidgets";
import { LinksDirectory } from "../links/LinksDirectory";
import { PersonalReminders, RevenueGrowth } from "./ExtraWidgets";
import { LockStar } from "../LockStar";

export const METRICS = {
  contacts: ["Contacts", Users], opportunities: ["Opportunities", Kanban], pipeline_value: ["Pipeline value", DollarSign, aud],
  won_value: ["Won value", Trophy, aud], open_incidents: ["Open incidents", ShieldAlert], appointments: ["Appointments", Calendar],
  submissions: ["Form submissions", FileCheck], unread: ["Unread messages", MessageSquareText], clients: ["Counselling clients", HeartHandshake],
  open_tasks: ["Open tasks", ListTodo],
  total_divisions: ["Total divisions", Building], total_staff: ["Total staff", Users], psych_risks: ["Psychological risks", Brain],
  overdue_training: ["Overdue training", TrendingUp], overdue_tasks: ["Overdue project tasks", Activity], my_reminders: ["Personal reminders", Heart],
};

const CARD_ICONS = {
  total_divisions: [Building, "#E0B44C"], total_staff: [Users, "#3B82F6"], open_incidents: [TriangleAlert, "#EF4444"],
  psych_risks: [Brain, "#E0B44C"], overdue_training: [TrendingUp, "#7C93C9"], overdue_tasks: [Activity, "#F59E0B"], my_reminders: [Heart, "#93B4E8"],
};

export const DEFAULT_SUMMARY_CARDS = [
  ["total_divisions", "Total Divisions"], ["total_staff", "Total Staff"], ["open_incidents", "Open Incidents"], ["psych_risks", "Psychological Risks"],
  ["overdue_training", "Overdue Training"], ["overdue_tasks", "Overdue Project Tasks"], ["my_reminders", "Personal Reminders"],
].map(([metric, label]) => ({ metric, label, icon_url: "" }));

export const WIDGETS = {
  greeting_banner: { label: "Greeting banner (Good morning + date & time)", defaults: { image_url: "", show_name: true, show_datetime: true, subtitle: "", overlay: 50 } },
  page_banner: { label: "Page banner (overline + title)", defaults: { image_url: "", overline: "BOS HQ Administration", title: "Dashboard", overlay: 50 } },
  datetime: { label: "Date & time", defaults: {} },
  stats: { label: "Stat cards", defaults: { metrics: ["contacts", "pipeline_value", "open_incidents", "appointments"] } },
  quick_links: { label: "Quick link tiles", defaults: { links: ["/app/contacts", "/app/calendar", "/app/record-centre", "/app/conversations"] } },
  pipeline_chart: { label: "Pipeline by stage chart", defaults: {} },
  upcoming_appointments: { label: "Upcoming appointments", defaults: {} },
  recent_activity: { label: "Recent activity", defaults: {} },
  note: { label: "Notice / text", defaults: { title: "Team notice", body: "" } },
  summary_cards: { label: "Summary cards (icons + live counters)", defaults: { cards: DEFAULT_SUMMARY_CARDS } },
  daily_quote: { label: "Daily message (a new quote every day)", defaults: { heading: "Daily Message" } },
  key_dates: { label: "Key dates (organisational reminders)", defaults: {} },
  important_links: { label: "Important links (search + organisation and personal links)", defaults: {} },
  library: { label: "From widget library (shared widgets)", defaults: { widget_def_id: "" } },
  personal_reminders: { label: "Personal reminders (private to each person)", defaults: {} },
  revenue_growth: { label: "Revenue growth since establishment (Xero)", defaults: { since_year: 2024 } },
};

const BANNERS = ["greeting_banner", "page_banner"];
const WIDTH = { two_thirds: "md:col-span-4", full: "md:col-span-6", half: "md:col-span-3", third: "md:col-span-2" };

export const greeting = (d = new Date()) => (d.getHours() < 12 ? "Good morning" : d.getHours() < 17 ? "Good afternoon" : "Good evening");

function useNow() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 15000); return () => clearInterval(t); }, []);
  return now;
}

const fmtDay = (d) => d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).replace(",", "");
const fmtTime = (d) => d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase();

function DateTimeText({ className = "" }) {
  const now = useNow();
  return <div className={className} data-testid="widget-datetime-text"><span>{fmtDay(now)}</span>{"\u00a0\u00a0 "}<span className="ml-3">{fmtTime(now)}</span></div>;
}

const src = (url) => (url.startsWith("/api") ? BACKEND_URL + url : url);

export function BannerFrame({ url, testid, overlay = 50, children }) {
  return (
    <div className="relative overflow-hidden bg-[#070d1c] border-b border-[#C2A462]/25" data-testid={testid}>
      {url ? <img src={src(url)} alt="" className="block w-full h-[110px] sm:h-[140px] lg:h-[170px] object-cover" /> : <div className="h-[110px] sm:h-[140px] lg:h-[170px]" style={bgStyle("")} />}
      <div className="absolute inset-0 bg-black" style={{ opacity: (overlay ?? 50) / 100 }} data-testid={`${testid}-overlay`} />
      {children}
    </div>
  );
}

export function PageHero({ url, overline, title, desc, overlay = 50, children }) {
  return (
    <div className="-mx-5 sm:-mx-8 -mt-5 sm:-mt-8 mb-8" data-testid="page-hero">
      <BannerFrame url={url} testid="page-hero-banner" overlay={overlay}>
        <div className="absolute left-[3%] top-1/2 -translate-y-1/2">
          {overline && <div className="text-[11px] sm:text-[12px] uppercase tracking-[0.24em] text-[#D9B95C]">{overline}</div>}
          <h1 className="mt-2 text-2xl sm:text-[1.9rem] lg:text-[2.2rem] font-medium tracking-wide text-white" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} data-testid="page-title">{title}</h1>
          {desc && <p className="mt-2 text-[13px] text-slate-300">{desc}</p>}
        </div>
        {children && <div className="absolute right-[3%] bottom-4 flex flex-wrap gap-2">{children}</div>}
      </BannerFrame>
    </div>
  );
}

const bgStyle = (url) => (url ? { backgroundImage: `url(${url.startsWith("/api") ? BACKEND_URL + url : url})` } : { background: "radial-gradient(ellipse at 20% 0%, #13213d 0%, #0b1428 50%, #070d1c 100%)" });

function GreetingBanner({ c }) {
  const { user } = useAuth();
  const now = useNow();
  return (
    <BannerFrame url={c.image_url} overlay={c.overlay} testid="widget-greeting-banner">
      {c.show_datetime && <DateTimeText className="absolute right-[4%] top-[9%] text-[10px] sm:text-[13px] font-bold uppercase tracking-[0.18em] sm:tracking-[0.22em] text-[#E3CC92]" />}
      <div className="absolute left-[3%] bottom-[14%]">
        <h2 className="text-xl sm:text-[1.8rem] lg:text-[2.1rem] font-medium tracking-wide text-white" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} data-testid="greeting-text">{greeting(now)}{c.show_name ? `, ${user.name.split(" ")[0]}` : ""}</h2>
        {c.subtitle && <p className="mt-2 text-sm text-[#E3CC92]">{c.subtitle}</p>}
      </div>
    </BannerFrame>
  );
}

export function PageBanner({ c }) {
  return (
    <BannerFrame url={c.image_url} overlay={c.overlay} testid="widget-page-banner">
      <div className="absolute left-[3%] top-1/2 -translate-y-1/2">
        {c.overline && <div className="text-[11px] sm:text-[12px] uppercase tracking-[0.24em] text-[#D9B95C]">{c.overline}</div>}
        <h2 className="mt-2 text-xl sm:text-[1.8rem] lg:text-[2.1rem] font-medium tracking-wide text-white" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{c.title}</h2>
      </div>
    </BannerFrame>
  );
}

const Card = ({ title, children, testid }) => (
  <div className="rounded-md border bg-card p-5 h-full" data-testid={testid}>{title && <div className="overline mb-4">{title}</div>}{children}</div>
);

const TILE = "w-[calc(50%-0.375rem)] sm:w-[calc(25%-0.5625rem)] lg:w-[calc((100%-4.5rem)/7)]";

function QuickLinks({ c }) {
  const items = (c.links || []).map((to) => ACCOUNT_NAV.find((n) => n.to === to)).filter(Boolean);
  return (
    <div className="flex flex-wrap justify-center gap-3" data-testid="widget-quick-links">
      {items.map(({ to, label, icon: Icon }) => (
        <Link key={to} to={to} className={`group flex items-center gap-2.5 rounded-md border bg-card px-3.5 py-2.5 transition-colors hover:border-[#C2A462]/60 ${TILE}`} data-testid={`quick-link-${to.split("/")[2]}`}>
          <Icon className="h-4 w-4 shrink-0 text-[#C2A462]" strokeWidth={1.6} />
          <span className="truncate text-[13px] text-slate-200 group-hover:text-[#EBD6A0]">{label}</span>
        </Link>
      ))}
    </div>
  );
}

function SummaryCards({ c, d }) {
  const cards = c.cards || [];
  return (
    <div className="flex flex-wrap justify-center gap-3" data-testid="widget-summary-cards">
      {cards.map((k, i) => {
        const [Icon, color] = CARD_ICONS[k.metric] || [METRICS[k.metric]?.[1] || ListTodo, "#C2A462"];
        const fmt = METRICS[k.metric]?.[2];
        const v = d ? d[k.metric] ?? 0 : "…";
        return (
          <div key={i} className={`rounded-md border bg-card px-4 py-3.5 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#C2A462]/40 ${TILE}`} data-testid={`summary-card-${k.metric}`}>
            <div className="flex items-center justify-between">
              {k.icon_url ? <img src={src(k.icon_url)} alt="" className="h-7 w-7 object-contain" /> : <Icon className="h-6 w-6" style={{ color }} strokeWidth={1.6} />}
              <span className="text-2xl font-display font-bold text-white tabular-nums" data-testid={`summary-value-${k.metric}`}>{fmt && d ? fmt(v) : v}</span>
            </div>
            <div className="mt-3 text-[13px] font-medium text-slate-300 leading-snug">{k.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function DailyQuote({ c }) {
  return (
    <div className="py-4 text-center" data-testid="widget-daily-quote">
      <div className="text-[15px] font-bold uppercase tracking-[0.18em] text-[#D9B95C]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 700 }}>{c.heading || "Daily Message"}</div>
      <p className="mt-2 text-base sm:text-[17px] text-white/90" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} data-testid="daily-quote-text">{quoteOfTheDay()}</p>
    </div>
  );
}

export function Widget({ w, d }) {
  const c = w.config || {};
  switch (w.type) {
    case "summary_cards": return <SummaryCards c={c} d={d} />;
    case "daily_quote": return <DailyQuote c={c} />;
    case "greeting_banner": return <GreetingBanner c={c} />;
    case "page_banner": return <PageBanner c={c} />;
    case "datetime": return <Card title="Today" testid="widget-datetime"><DateTimeText className="text-xl font-display font-bold text-[#EBD6A0] flex flex-col gap-1" /></Card>;
    case "key_dates": return <KeyDatesWidget />;
    case "important_links": return <LinksDirectory compact />;
    case "library": return <LibraryWidget c={c} d={d} />;
    case "personal_reminders": return <PersonalReminders preview={String(w.id || "").startsWith("preview-")} />;
    case "revenue_growth": return <RevenueGrowth c={c} />;
    case "quick_links": return <QuickLinks c={c} />;
    case "note": return <Card title={c.title} testid="widget-note"><p className="text-sm text-slate-300 whitespace-pre-wrap">{c.body}</p></Card>;
    case "stats": return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="widget-stats">
        {(c.metrics || []).filter((m) => METRICS[m]).map((m) => { const [label, icon, f] = METRICS[m]; return <Stat key={m} label={label} icon={icon} value={d ? (f ? f(d[m]) : d[m]) : "…"} testid={`stat-${m}`} />; })}
      </div>
    );
    case "pipeline_chart": return (
      <Card title="Pipeline by stage (AUD)" testid="widget-pipeline-chart">
        <div className="h-64"><ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
          <BarChart data={d?.by_stage || []}><CartesianGrid strokeDasharray="3 3" stroke="hsl(40 18% 14%)" /><XAxis dataKey="stage" stroke="#94a3b8" fontSize={11} /><YAxis stroke="#94a3b8" fontSize={11} />
            <Tooltip contentStyle={{ background: "#0b1428", border: "1px solid #C2A46255" }} formatter={(v) => aud(v)} /><Bar dataKey="value" fill="#C2A462" radius={[3, 3, 0, 0]} /></BarChart>
        </ResponsiveContainer></div>
      </Card>
    );
    case "upcoming_appointments": return (
      <Card title="Upcoming appointments" testid="widget-upcoming">
        {!d?.upcoming?.length && <div className="text-sm text-muted-foreground">Nothing booked.</div>}
        {(d?.upcoming || []).map((a) => <div key={a.id} className="border-b py-2 text-sm"><div>{a.title}</div><div className="text-xs text-muted-foreground font-mono">{fmtDate(a.start)}</div></div>)}
      </Card>
    );
    case "recent_activity": return (
      <Card title="Recent activity (audit trail)" testid="widget-recent-activity">
        {(d?.recent || []).map((r) => (
          <div key={r.id} className="flex justify-between border-b py-2 text-sm gap-4">
            <span><span className="text-primary font-mono">{r.action}</span> {r.entity} <span className="text-muted-foreground">{r.detail}</span></span>
            <span className="text-xs text-muted-foreground whitespace-nowrap">{r.user_email} · {fmtDate(r.created_at)}</span>
          </div>
        ))}
      </Card>
    );
    default: return null;
  }
}

export function DashboardRenderer({ dashboard, data, edit, onMove, onWidth, slot }) {
  const [drag, setDrag] = useState(null);
  const slotAfterFirst = BANNERS.includes(dashboard.widgets?.[0]?.type);
  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-4" data-testid="dashboard-renderer">
      {!slotAfterFirst && slot}
      {(dashboard.widgets || []).map((w, i) => {
        const banner = BANNERS.includes(w.type);
        const bleed = banner ? `md:col-span-6 -mx-5 sm:-mx-8 ${i === 0 ? "-mt-5 sm:-mt-8" : ""}` : WIDTH[w.width] || WIDTH.full;
        const movable = edit && !banner;
        return (
          <Fragment key={w.id}>
          <div className={`${bleed} fade-up relative ${movable ? "rounded-md ring-1 ring-dashed ring-[#C2A462]/50 cursor-move" : ""} ${drag === w.id ? "opacity-40" : ""}`}
            draggable={movable} onDragStart={() => setDrag(w.id)} onDragEnd={() => setDrag(null)}
            onDragOver={(e) => movable && e.preventDefault()} onDrop={() => { if (movable && drag && drag !== w.id) onMove(drag, w.id); setDrag(null); }}
            data-testid={`dash-widget-${w.id}`}>
            {!edit && <LockStar id={`widget:${w.type}`} className="absolute right-2 top-2 z-20" />}
            {movable && (
              <div className="absolute -top-3 right-3 z-20 flex items-center gap-1 rounded-full border border-[#C2A462]/60 bg-[#0b1428] px-2 py-1 shadow-lg" data-testid={`widget-toolbar-${w.id}`}>
                <GripVertical className="h-3.5 w-3.5 text-[#C2A462]" />
                {[["third", "⅓"], ["half", "½"], ["two_thirds", "⅔"], ["full", "Full"]].map(([k, l]) => (
                  <button key={k} onClick={() => onWidth(w.id, k)} data-testid={`widget-width-${w.id}-${k}`}
                    className={`rounded-full px-2 text-[11px] font-semibold transition-colors ${ (w.width || "full") === k ? "bg-[#C2A462] text-[#0b1428]" : "text-[#C9AD6E] hover:bg-[#C2A462]/15"}`}>{l}</button>
                ))}
              </div>
            )}
            <div className={movable ? "pointer-events-none select-none" : ""}><Widget w={w} d={data} /></div>
          </div>
          {i === 0 && slotAfterFirst && slot}
          </Fragment>
        );
      })}
      {!dashboard.widgets?.length && <div className="md:col-span-6 text-sm text-muted-foreground">This dashboard has no widgets yet.</div>}
    </div>
  );
}
